import { Button } from "@fp/ui/components/base/buttons/button";
import { Input } from "@fp/ui/components/base/input/input";
import {
  PhoneInput,
} from "@fp/ui/components/base/input/input-phone";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axios from "@/lib/axios";
import {
  ArrowLeft,
  Building07,
  Mail01,
  Phone01,
} from "@untitledui/icons";
import { useUserContext } from "@/components/user-context";
import { Link } from "@tanstack/react-router";

interface CreateOrganizationDto {
  name: string;
  isActive: boolean;
}

interface RequestByContactDto {
  email?: string;
  phone?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
  requesterPhone?: string;
}

export const Route = createFileRoute("/onboarding")({
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { claims, refetchOrganizations, logout, hasOrganizations } = useUserContext();
  const [view, setView] = useState<"select" | "create-org" | "join-request">(
    "select",
  );
  const [orgName, setOrgName] = useState("");
  const [lookupType, setLookupType] = useState<"email" | "phone">("email");
  const [contactValue, setContactValue] = useState("");
  const [requesterFirstName, setRequesterFirstName] = useState(claims?.firstName ?? "");
  const [requesterLastName, setRequesterLastName] = useState(claims?.lastName ?? "");
  const [requesterPhone, setRequesterPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: userProfile } = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const response = await axios.get<{ firstName: string | null; lastName: string | null; phone: string | null }>("/api/users/me");
      return response.data;
    },
  });

  useEffect(() => {
    if (userProfile) {
      if (userProfile.firstName) setRequesterFirstName(userProfile.firstName);
      if (userProfile.lastName) setRequesterLastName(userProfile.lastName);
      if (userProfile.phone) setRequesterPhone(userProfile.phone);
    }
  }, [userProfile]);

  const createOrgMutation = useMutation({
    mutationFn: async (data: CreateOrganizationDto) => {
      const response = await axios.post("/api/organizations", data);
      return response.data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries();
      await refetchOrganizations();
      router.navigate({ to: "/dashboard" });
    },
    onError: (err: any) => {
      setError(
        err.response?.data?.message ||
          "Failed to create organization. Please try again.",
      );
    },
  });

  const requestByContactMutation = useMutation({
    mutationFn: async (data: RequestByContactDto) => {
      const response = await axios.post(
        "/api/join-requests/request-by-contact",
        data,
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setContactValue("");
    },
    onError: (err: any) => {
      const status = err.response?.status;
      const message = err.response?.data?.message || err.response?.data?.title;

      if (status === 403) {
        setError("You must verify your email before requesting to join an organization.");
      } else if (status === 429) {
        setError("Too many requests. Please try again later.");
      } else {
        setError(message || "Failed to send request. Please try again.");
      }
    },
  });

  const handleCreateOrg = () => {
    setError(null);
    if (!orgName.trim()) {
      setError("Please enter an organization name.");
      return;
    }
    createOrgMutation.mutate({ name: orgName.trim(), isActive: true });
  };

  const handleRequestByContact = () => {
    setError(null);
    if (!contactValue.trim()) {
      setError(
        lookupType === "email"
          ? "Please enter an email address."
          : "Please enter a phone number.",
      );
      return;
    }

    if (!requesterFirstName.trim() || !requesterLastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }

    const data: RequestByContactDto = {
      ...(lookupType === "email"
        ? { email: contactValue.trim() }
        : { phone: contactValue.trim() }),
      requesterFirstName: requesterFirstName.trim(),
      requesterLastName: requesterLastName.trim(),
      requesterPhone: requesterPhone.trim() || undefined,
    };

    requestByContactMutation.mutate(data);
  };

  const renderSelectView = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {hasOrganizations
            ? "Add Another Organization"
            : "Welcome to FieldPay"}
        </h1>
        <p className="text-gray-600">
          {hasOrganizations
            ? "Create a new organization or request to join an existing one."
            : "To get started, create your own organization or request to join an existing one."}
        </p>
      </div>

      <button
        type="button"
        onClick={() => setView("create-org")}
        className="w-full p-6 bg-white border border-gray-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-colors text-left"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 bg-brand-100 rounded-lg">
            <Building07 className="size-6 text-brand-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Create an Organization
            </h2>
            <p className="text-sm text-gray-600">
              Start a new organization and become its owner. You can invite team
              members later.
            </p>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={() => setView("join-request")}
        className="w-full p-6 bg-white border border-gray-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-colors text-left"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 bg-brand-100 rounded-lg">
            <Mail01 className="size-6 text-brand-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Request to Join
            </h2>
            <p className="text-sm text-gray-600">
              Enter the email or phone number of someone you know to find their
              organization.
            </p>
          </div>
        </div>
      </button>
    </div>
  );

  const renderCreateOrgView = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Create Your Organization
        </h1>
        <p className="text-gray-600">
          Give your organization a name to get started.
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4">
        <Input
          label="Organization Name"
          placeholder="Enter organization name"
          value={orgName}
          onChange={(value: string) => {
            setOrgName(value);
            setError(null);
          }}
          isInvalid={!!error}
        />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <Button
            color="secondary"
            onClick={() => {
              setView("select");
              setError(null);
              setOrgName("");
            }}
            className="flex-1"
          >
            Back
          </Button>
          <Button
            color="primary"
            onClick={handleCreateOrg}
            isLoading={createOrgMutation.isPending}
            isDisabled={createOrgMutation.isPending}
            className="flex-1"
          >
            Create Organization
          </Button>
        </div>
      </div>
    </div>
  );

  const renderJoinRequestView = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Request to Join
        </h1>
        <p className="text-gray-600">
          Enter the email or phone number of someone you know who uses FieldPay.
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4">
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => {
              setLookupType("email");
              setContactValue("");
              setError(null);
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border transition-colors ${
              lookupType === "email"
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <Mail01 className="size-4" />
            <span className="text-sm font-medium">Email</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setLookupType("phone");
              setContactValue("");
              setError(null);
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border transition-colors ${
              lookupType === "phone"
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <Phone01 className="size-4" />
            <span className="text-sm font-medium">Phone</span>
          </button>
        </div>

        {lookupType === "email" ? (
          <Input
            label="Email Address"
            type="email"
            placeholder="colleague@company.com"
            value={contactValue}
            onChange={(value: string) => {
              setContactValue(value);
              setError(null);
            }}
            isInvalid={!!error}
          />
        ) : (
          <PhoneInput
            label="Phone Number"
            placeholder="(555) 123-4567"
            value={contactValue}
            onChange={(value: string) => {
              setContactValue(value);
              setError(null);
            }}
            isInvalid={!!error}
          />
        )}

      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Your Information</h3>

        <div className="flex gap-3">
          <Input
            label="First Name *"
            placeholder="First name"
            value={requesterFirstName}
            onChange={(value: string) => {
              setRequesterFirstName(value);
              setError(null);
            }}
            className="flex-1"
          />
          <Input
            label="Last Name *"
            placeholder="Last name"
            value={requesterLastName}
            onChange={(value: string) => {
              setRequesterLastName(value);
              setError(null);
            }}
            className="flex-1"
          />
        </div>

        <PhoneInput
          label="Phone Number"
          placeholder="(555) 123-4567"
          value={requesterPhone}
          onChange={(value: string) => {
            setRequesterPhone(value);
            setError(null);
          }}
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

<div className="flex gap-3">
        <Button
          color="secondary"
          onClick={() => {
            setView("select");
            setContactValue("");
            setRequesterFirstName("");
            setRequesterLastName("");
            setRequesterPhone("");
            setError(null);
          }}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          color="primary"
          onClick={handleRequestByContact}
          isLoading={requestByContactMutation.isPending}
          isDisabled={requestByContactMutation.isPending}
          className="flex-1"
        >
          Send Request
        </Button>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <PageContentContainer className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <img
              src="/Logo-green-bg.svg"
              alt="FieldPay"
              className="w-16 h-16 mx-auto mb-4"
            />
          </div>

          {view === "select" && renderSelectView()}
          {view === "create-org" && renderCreateOrgView()}
          {view === "join-request" && renderJoinRequestView()}

          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-center gap-4 text-sm">
            {hasOrganizations ? (
              <button
                type="button"
                onClick={() => router.history.back()}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-700"
              >
                <ArrowLeft className="size-4" />
                Back
              </button>
            ) : (
              <>
                <Link
                  to="/profile"
                  className="text-brand-600 hover:text-brand-700"
                >
                  My Profile
                </Link>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={logout}
                  className="text-gray-600 hover:text-gray-700"
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}